import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  useContext
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  useTheme,
  Button,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { ProductItem, ProductItemListProps } from "./types/products";
import ProductItemRow from "./ProductItemRow";
import NoProductItemsFound from "./NoProductItemsFound";
import AddProductItemModal from "./AddProductItemModal";
import TransitionProductItemModal from "./TransitionProductItemModal";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  PAGE_PORTAL_INVENTORY_PRODUCT_ITEM,
  PORTAL_PREFIX
} from "@kivunova/kivufrontendcommon";
import {
  useGetProductItemList,
  isUserCompanyAdmin
} from "../../hooks/api/useProductItem";
import { ProductItemStatus } from "./enums/productItems";
import {
  getInventoryQuantityPrefix,
  getProductUnitLabel
} from "./utils/getProductInventoryStatCardDescription";
import { formatProductItemStatus } from "./utils/productUtils";
import { PRODUCT_ITEM_QUANTITY_COLUMN_SX } from "./constants/productItemListTable";
import {
  itemMatchesInventoryStatusFilter,
  usesQuantityBasedStatusFilter
} from "./utils/productItemInventoryFilter";
import { fetchAllProductItemsByProduct } from "./utils/fetchAllProductItemsByProduct";
import { setProductItemListWithTotal } from "../../store/slices/productItemSlice";

const ProductItemList: React.FC<ProductItemListProps> = ({
  product,
  statusFilter: statusFilterProp,
  onStatusFilterChange
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { setAlert } = useContext(AlertBannerContext);
  const { productItemList } = useSelector(
    (state: RootState) => state.productItems
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useKivunovaTranslation();
  const [search, setSearch] = useState("");
  const [internalStatusFilter, setInternalStatusFilter] = useState("");
  const isStatusFilterControlled = onStatusFilterChange !== undefined;
  const statusFilter = isStatusFilterControlled
    ? (statusFilterProp ?? "")
    : internalStatusFilter;
  const setStatusFilter = (value: string) => {
    if (isStatusFilterControlled) {
      onStatusFilterChange(value);
    } else {
      setInternalStatusFilter(value);
    }
  };
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const quantityBasedStatusFilter = usesQuantityBasedStatusFilter(
    statusFilter,
    product
  );
  const [isFetchingAllItems, setIsFetchingAllItems] = useState(false);
  const [openAddItemModal, setOpenAddItemModal] = useState(false);
  const [cloneSourceItem, setCloneSourceItem] = useState<ProductItem | null>(
    null
  );
  const [openTransitionModal, setOpenTransitionModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProductItem | null>(null);

  const { mutate: mutateProductItemList, isPending: isProductItemListPending } =
    useGetProductItemList();
  const { productItemTotalElements: productItemTotalCount } = useSelector(
    (state: RootState) => state.productItems
  );
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  /** COMPANY_ADMIN: scope items to the catalog product’s store; others rely on token scope. */
  const itemListStoreId = useMemo(() => {
    if (!isUserCompanyAdmin(userDetails)) return undefined;
    const sid = product.storeId?.trim();
    return sid && sid.length > 0 ? sid : undefined;
  }, [userDetails, product.storeId]);

  // Debounce search to avoid excessive API calls; reset page with debounced value (not on initial mount)
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const isFirstSearchDebounce = useRef(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      if (!isFirstSearchDebounce.current) {
        setPage(1);
      }
      isFirstSearchDebounce.current = false;
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const listFetchKey = quantityBasedStatusFilter
    ? `${product.id}|${statusFilter}|${debouncedSearch}|quantity`
    : `${product.id}|${statusFilter}|${debouncedSearch}|${page}|paged`;
  const listFetchKeyRef = useRef(listFetchKey);
  listFetchKeyRef.current = listFetchKey;

  const fetchAllItemsForQuantityFilter = useCallback(async () => {
    if (!product.id) return;

    const requestKey = `${product.id}|${statusFilter}|${debouncedSearch}|quantity`;
    setIsFetchingAllItems(true);
    try {
      const items = await fetchAllProductItemsByProduct(product.id, setAlert, {
        nameSearch: debouncedSearch.trim() || undefined,
        storeId: itemListStoreId
      });
      if (listFetchKeyRef.current !== requestKey) {
        return;
      }
      dispatch(
        setProductItemListWithTotal({
          items,
          totalElements: items.length,
          totalPages: 1,
          currentPage: 0,
          pageSize: items.length
        })
      );
    } finally {
      const isCurrentQuantityFetch = listFetchKeyRef.current === requestKey;
      const switchedAwayFromQuantityFetch =
        !listFetchKeyRef.current.endsWith("|quantity");
      if (isCurrentQuantityFetch || switchedAwayFromQuantityFetch) {
        setIsFetchingAllItems(false);
      }
    }
  }, [
    product.id,
    statusFilter,
    debouncedSearch,
    itemListStoreId,
    setAlert,
    dispatch
  ]);

  const fetchPagedItems = useCallback(() => {
    if (!product.id) return;

    mutateProductItemList({
      productId: product.id,
      nameSearch: debouncedSearch.trim() || undefined,
      status: statusFilter || undefined,
      page: page - 1,
      pageSize: itemsPerPage,
      storeId: itemListStoreId
    });
  }, [
    product.id,
    debouncedSearch,
    statusFilter,
    page,
    itemsPerPage,
    itemListStoreId,
    mutateProductItemList
  ]);

  const refreshItems = useCallback(async () => {
    if (quantityBasedStatusFilter) {
      await fetchAllItemsForQuantityFilter();
    } else {
      fetchPagedItems();
    }
  }, [
    quantityBasedStatusFilter,
    fetchAllItemsForQuantityFilter,
    fetchPagedItems
  ]);

  const isFirstProductEffect = useRef(true);
  useEffect(() => {
    if (isFirstProductEffect.current) {
      isFirstProductEffect.current = false;
      return;
    }
    setPage(1);
  }, [product.id]);

  useEffect(() => {
    if (!product.id) return;
    refreshItems();
  }, [product.id, listFetchKey, refreshItems]);

  useEffect(() => {
    if (!quantityBasedStatusFilter) return;
    setPage(1);
  }, [quantityBasedStatusFilter, statusFilter, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const itemsForProduct = useMemo(() => {
    let items = productItemList.filter((item) => item.productId === product.id);
    if (quantityBasedStatusFilter && statusFilter) {
      items = items.filter((item) =>
        itemMatchesInventoryStatusFilter(item, statusFilter, product)
      );
    }
    return items;
  }, [productItemList, product, quantityBasedStatusFilter, statusFilter]);

  const visibleItems = useMemo(() => {
    if (!quantityBasedStatusFilter) {
      return itemsForProduct;
    }
    const start = (page - 1) * itemsPerPage;
    return itemsForProduct.slice(start, start + itemsPerPage);
  }, [itemsForProduct, quantityBasedStatusFilter, page, itemsPerPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      (quantityBasedStatusFilter
        ? itemsForProduct.length
        : (productItemTotalCount ?? itemsForProduct.length)) / itemsPerPage
    )
  );

  const handleView = (itemId: string) => {
    const route = PAGE_PORTAL_INVENTORY_PRODUCT_ITEM.replace(
      ":productId",
      product.id
    ).replace(":itemId", itemId);
    navigate(`/${PORTAL_PREFIX}/${route}`);
  };

  const handleReceiveStock = () => {
    setCloneSourceItem(null);
    setOpenAddItemModal(true);
  };

  const handleCloneItem = (itemId: string) => {
    const item = productItemList.find((row) => row?.id === itemId) ?? null;
    setCloneSourceItem(item);
    setOpenAddItemModal(true);
  };

  const handleTransition = (item: ProductItem) => {
    setSelectedItem(item);
    setOpenTransitionModal(true);
  };

  const handleAddItem = async () => {
    try {
      await refreshItems();
      await queryClient.invalidateQueries({
        queryKey: ["productInventorySummary", product.id]
      });
    } catch (error) {
      console.error("Failed to refresh product items:", error);
    }
  };

  const isListLoading = isProductItemListPending || isFetchingAllItems;
  const isEmpty = !visibleItems.length;
  const isInitialLoad = isListLoading && visibleItems.length === 0;

  const unitLabel = useMemo(
    () => getProductUnitLabel(product, t),
    [product, t]
  );
  const quantityUnitLabel = useMemo(
    () => getInventoryQuantityPrefix(product.sellingMode, unitLabel, t),
    [product.sellingMode, unitLabel, t]
  );
  const totalQuantityHeader = t(
    "product-item-list-col-total-quantity",
    `Total (${quantityUnitLabel})`,
    { unit: quantityUnitLabel }
  );
  const remainingQuantityHeader = t(
    "product-item-list-col-remaining-quantity",
    `Remaining (${quantityUnitLabel})`,
    { unit: quantityUnitLabel }
  );

  // Show full-page loader only on initial load; keep modals mounted during background refresh
  if (isInitialLoad) {
    return (
      <CommonLoader
        loadingMessage={t(
          "product-item-list-loading",
          "Loading product items..."
        )}
      />
    );
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{ width: "100%", color: "text.primary" }}
    >
      {/* Toolbar */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          mb: 3
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexDirection={matchMD ? "column" : "row"}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: `${BRAND_COLOR}14`
              }}
            >
              <LayersOutlinedIcon sx={{ color: BRAND_COLOR, fontSize: 22 }} />
            </Box>
            <Typography variant="h5" fontWeight="700" color="text.primary">
              {t("product-item-list-title", "Product Items")}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            flexDirection={matchMD ? "column" : "row"}
            flexWrap="wrap"
            width={matchMD ? "100%" : "auto"}
          >
            <TextField
              size="small"
              placeholder={t(
                "product-item-list-search-placeholder",
                "Search items..."
              )}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: matchMD ? "100%" : 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon
                      sx={{ fontSize: 20, color: "text.disabled" }}
                    />
                  </InputAdornment>
                ),
                sx: { borderRadius: 1.5 }
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="status-filter-label">
                {t("product-item-list-status-filter", "Status")}
              </InputLabel>
              <Select
                labelId="status-filter-label"
                label={t("product-item-list-status-filter", "Status")}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                renderValue={(value) =>
                  value === ""
                    ? t("product-item-list-status-all", "All")
                    : formatProductItemStatus(String(value), t)
                }
                sx={{ borderRadius: 1.5 }}
              >
                <MenuItem value="">
                  {t("product-item-list-status-all", "All")}
                </MenuItem>
                {Object.values(ProductItemStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {formatProductItemStatus(status, t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!isEmpty && (
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleReceiveStock}
                data-testid="product-item-list-add-btn"
                sx={{
                  backgroundColor: BRAND_COLOR,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: `0 2px 8px ${BRAND_COLOR}40`,
                  "&:hover": {
                    backgroundColor: BRAND_HOVER,
                    boxShadow: `0 4px 12px ${BRAND_COLOR}50`
                  }
                }}
              >
                {t("product-item-list-add-item-button", "Add new item(s)")}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <AnimatePresence mode="wait">
        {isEmpty ? (
          <NoProductItemsFound onAddItem={handleReceiveStock} />
        ) : (
          <Box
            component={motion.div}
            key="content"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Table */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                boxShadow: "none"
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: 14 }}>
                      {t("product-item-list-col-item-name", "Item name")}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...PRODUCT_ITEM_QUANTITY_COLUMN_SX,
                        fontWeight: 600,
                        fontSize: 14
                      }}
                    >
                      {totalQuantityHeader}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...PRODUCT_ITEM_QUANTITY_COLUMN_SX,
                        fontWeight: 600,
                        fontSize: 14
                      }}
                    >
                      {remainingQuantityHeader}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 14 }}>
                      {t("product-item-list-col-status", "Status")}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, fontSize: 14 }}
                    >
                      {t("product-item-list-col-qr-code", "QR Code")}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, fontSize: 14 }}
                    >
                      {t("product-item-list-col-actions", "Actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleItems.map((item) => (
                    <ProductItemRow
                      key={item?.id}
                      item={item}
                      product={product}
                      onView={handleView}
                      onCloneItem={handleCloneItem}
                      onTransition={handleTransition}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mt={3}
                mb={3}
              >
                <Pagination
                  count={totalPages}
                  page={Math.min(page, totalPages)}
                  onChange={(_, value) => setPage(value as number)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        )}
      </AnimatePresence>

      <AddProductItemModal
        open={openAddItemModal}
        onClose={() => {
          setOpenAddItemModal(false);
          setCloneSourceItem(null);
        }}
        cloneFromItem={cloneSourceItem ?? undefined}
        product={product}
        productCatalogStoreId={product.storeId}
        onSubmit={handleAddItem}
        isLoading={false}
      />

      {/* Transition Product Item Modal */}
      {openTransitionModal && selectedItem && (
        <TransitionProductItemModal
          open={openTransitionModal}
          onClose={() => {
            setOpenTransitionModal(false);
            setSelectedItem(null);
          }}
          item={selectedItem as ProductItem}
          onSuccess={() => {
            refreshItems();
          }}
        />
      )}
    </Box>
  );
};

export default ProductItemList;
